import {inject, Injectable} from '@angular/core';
import {from, Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {
    addDoc,
    collection,
    collectionChanges,
    collectionData,
    deleteDoc,
    doc,
    DocumentChange,
    DocumentData,
    Firestore,
    getCountFromServer,
    getDoc,
    getDocs,
    limit,
    orderBy,
    query,
    QueryConstraint,
    QueryDocumentSnapshot,
    startAfter,
    setDoc,
    updateDoc,
    writeBatch,
} from '@angular/fire/firestore';
import {PaginationOutDto} from "../../tal-library/table/lib-table/lib-table.component";
import {SearchOutDto, SORT_BY_DIRECTION} from "../../talenlio-hireos/home-layout/common/model/search.model";
import {LibTableUtil} from "../../tal-library/table/common/util/lib-table.util";


@Injectable({
    providedIn: 'root',
})
export class FirestoreWrapperService {
    private readonly ID = 'id';
    private readonly DESC = 'desc';
    private readonly ADDED = 'added';

    private firestore = inject(Firestore);
    private paginationCursors: Map<string, Map<number, QueryDocumentSnapshot<DocumentData>>> = new Map();

    search<T>(collectionName: string, fireStoreSearchInDto: FireStoreSearchInDto): Observable<SearchOutDto<T>> {
        const {pageIndex = 0, constraints = []} = fireStoreSearchInDto;
        const queryPage = this.buildPaginatedQuery(collectionName, fireStoreSearchInDto, constraints);
        if (pageIndex > 0 && !queryPage) {
            return of(this.buildEmptySearchResponse<T>(fireStoreSearchInDto));
        }
        const queryCount = query(collection(this.firestore, collectionName), ...constraints);
        return from(Promise.all([getDocs(queryPage!), getCountFromServer(queryCount)])).pipe(
            map(([snapshot, countSnapshot]) => {
                this.updateCursor(collectionName, pageIndex, snapshot.docs);
                return this.mapToSearchOutDto<T>(snapshot.docs, countSnapshot.data().count, fireStoreSearchInDto);
            }),
        );
    }

    get<T>(collectionName: string, id: string): Observable<T | undefined> {
        const docRef = doc(this.firestore, `${collectionName}/${id}`);
        return from(getDoc(docRef)).pipe(
            map(snapshot => (snapshot.exists() ? ({id: snapshot.id, ...snapshot.data()} as T) : undefined)),
        );
    }

    add<T>(collectionName: string, data: T): Observable<string> {
        const collectionReference = collection(this.firestore, collectionName);
        return from(addDoc(collectionReference, data as any)).pipe(map(docRef => docRef.id));
    }

    update<T>(collectionName: string, id: string, data: Partial<T>): Promise<void> {
        const documentReference = doc(this.firestore, `${collectionName}/${id}`);
        return updateDoc(documentReference, data as any);
    }

    set<T>(collectionName: string, id: string, data: Partial<T>): Promise<void> {
        const documentReference = doc(this.firestore, `${collectionName}/${id}`);
        return setDoc(documentReference, data as any, {merge: true});
    }

    delete(collectionName: string, id: string): Promise<void> {
        const documentReference = doc(this.firestore, `${collectionName}/${id}`);
        return deleteDoc(documentReference);
    }

    listen<T>(collectionName: string): Observable<T[]> {
        const ref = collection(this.firestore, collectionName);
        const searchQuery = query(ref, orderBy(this.ID, this.DESC));
        return collectionData(searchQuery, {idField: this.ID}) as Observable<T[]>;
    }

    listenAddEvent<T>(collectionName: string): Observable<DocumentChange<DocumentData>[]> {
        const ref = collection(this.firestore, collectionName);
        return collectionChanges(ref, {events: [this.ADDED]});
    }

    async batchUpdate(collectionName: string, docsToUpdate: { id: string, data: any }[]): Promise<void> {
        const batch = writeBatch(this.firestore);
        docsToUpdate.forEach(item => {
            const docRef = doc(this.firestore, `${collectionName}/${item.id}`);
            batch.update(docRef, item.data);
        });
        return batch.commit();
    }

    async batchDelete(collectionName: string, ids: string[]): Promise<void> {
        const batch = writeBatch(this.firestore);
        ids.forEach(id => {
            const docRef = doc(this.firestore, `${collectionName}/${id}`);
            batch.delete(docRef);
        });
        return batch.commit();
    }

    private buildPaginatedQuery(collectionName: string, options: FireStoreSearchInDto, constraints: QueryConstraint[]) {
        const {pageIndex = 0, pageSize = 10, sortBy = this.ID, direction = SORT_DIRECTION.DESC} = options;
        const allConstraints = [orderBy(sortBy, direction), ...constraints];
        let searchQuery = query(collection(this.firestore, collectionName), ...allConstraints);
        if (pageIndex > 0) {
            const lastDoc = this.paginationCursors.get(collectionName)?.get(pageIndex - 1);
            if (!lastDoc) return null;
            searchQuery = query(searchQuery, startAfter(lastDoc));
        }
        return query(searchQuery, limit(pageSize));
    }

    private mapToSearchOutDto<T>(documents: QueryDocumentSnapshot<DocumentData>[], totalElements: number, options: FireStoreSearchInDto): SearchOutDto<T> {
        const {
            pageIndex = LibTableUtil.DEFAULT_PAGE_INDEX,
            pageSize = LibTableUtil.DEFAULT_PAGE_SIZE_20,
            sortBy = this.ID,
            direction = SORT_DIRECTION.DESC,
            searchText = ''
        } = options;
        return {
            facets: undefined,
            data: documents.map(document => ({id: document.id, ...document.data()} as T)),
            paginationOutDto: this.buildPaginationDto(totalElements, pageIndex, pageSize),
            sortBy: sortBy,
            direction: direction === SORT_DIRECTION.ASC ? SORT_BY_DIRECTION.ASC : SORT_BY_DIRECTION.DESC,
            searchText: searchText
        };
    }

    private buildEmptySearchResponse<T>(options: FireStoreSearchInDto): SearchOutDto<T> {
        return {
            ...this.mapToSearchOutDto<T>([], 0, options),
            paginationOutDto: this.buildPaginationDto(0, options.pageIndex, options.pageSize),
        };
    }

    private buildPaginationDto(totalElements: number, pageIndex: number, pageSize: number): PaginationOutDto {
        return {
            pageIndex,
            pageSize,
            totalElements,
            totalPages: Math.ceil(totalElements / pageSize),
        };
    }

    private updateCursor(collectionName: string, pageIndex: number, docs: any[]) {
        if (docs.length === 0) return;
        let collectionCursors = this.paginationCursors.get(collectionName);
        if (!collectionCursors) {
            collectionCursors = new Map();
            this.paginationCursors.set(collectionName, collectionCursors);
        }
        collectionCursors.set(pageIndex, docs[docs.length - 1]);
    }

}

export class FireStoreSearchInDto {
    pageIndex?: number;
    pageSize?: number;
    sortBy?: string;
    direction?: 'asc' | 'desc';
    constraints?: QueryConstraint[];
    searchText?: string;
}

export enum SORT_DIRECTION {
    ASC = 'asc',
    DESC = 'desc'
}
